import { type Room, getStateCallbacks } from 'colyseus.js';
import type { Container } from 'pixi.js';
import { INTERPOLATION_BUFFER_MS, MessageType, getAICharacterByName } from '@wooorms/shared';
import type { SegmentData, LeaderboardEntry, WormSkinData } from '@wooorms/shared';
import { WormRenderer, AccessoryType } from '../game/entities/WormRenderer';
import { FoodRenderer } from '../game/entities/FoodRenderer';
import { PowerupRenderer } from '../game/entities/PowerupRenderer';
import { ParticleSystem } from '../game/effects/ParticleSystem';

/**
 * Segment with velocity for smooth interpolation
 */
interface SegmentWithVelocity extends SegmentData {
  vx: number;
  vy: number;
}

/**
 * Interpolation snapshot for smooth remote entity movement
 * Includes velocity data for Hermite interpolation
 */
interface InterpolationSnapshot {
  timestamp: number;
  segments: SegmentWithVelocity[];
  angle: number;
  boosting: boolean;
}

/**
 * Circular buffer for efficient snapshot storage
 * O(1) push and trim operations instead of O(n) shift
 * Pre-allocates snapshot objects and segment arrays to avoid GC pressure
 */
class SnapshotBuffer {
  private buffer: InterpolationSnapshot[];
  private head: number = 0;
  private tail: number = 0;
  private _size: number = 0;
  private readonly capacity: number;
  /** Track which slots have valid data */
  private valid: boolean[];

  constructor(capacity: number = 20) {
    this.capacity = capacity;
    // Pre-allocate all snapshot objects to avoid GC
    this.buffer = new Array(capacity);
    this.valid = new Array(capacity).fill(false);
    for (let i = 0; i < capacity; i++) {
      this.buffer[i] = {
        timestamp: 0,
        segments: [], // Will be resized as needed
        angle: 0,
        boosting: false,
      };
    }
  }

  get size(): number {
    return this._size;
  }

  /**
   * Get the next slot to write to (for in-place filling)
   * Call commitPush() after filling to finalize
   */
  getNextSlot(): InterpolationSnapshot {
    return this.buffer[this.tail]!;
  }

  /**
   * Commit the push after filling the slot from getNextSlot()
   */
  commitPush(): void {
    this.valid[this.tail] = true;
    this.tail = (this.tail + 1) % this.capacity;

    if (this._size < this.capacity) {
      this._size++;
    } else {
      // Buffer is full, advance head (overwrite oldest)
      this.valid[this.head] = false;
      this.head = (this.head + 1) % this.capacity;
    }
  }

  /** Legacy push for compatibility - prefer getNextSlot/commitPush for efficiency */
  push(snapshot: InterpolationSnapshot): void {
    const slot = this.buffer[this.tail]!;
    slot.timestamp = snapshot.timestamp;
    slot.angle = snapshot.angle;
    slot.boosting = snapshot.boosting;
    // Copy segments into the pre-allocated slot
    this.copySegments(slot.segments, snapshot.segments);
    this.commitPush();
  }

  /**
   * Copy segments into existing array, resizing if needed
   */
  private copySegments(target: SegmentWithVelocity[], source: SegmentWithVelocity[]): void {
    // Resize target array if needed
    while (target.length < source.length) {
      target.push({ x: 0, y: 0, radius: 0, vx: 0, vy: 0 });
    }
    // Copy values (reuse existing objects)
    for (let i = 0; i < source.length; i++) {
      const t = target[i]!;
      const s = source[i]!;
      t.x = s.x;
      t.y = s.y;
      t.radius = s.radius;
      t.vx = s.vx;
      t.vy = s.vy;
    }
    // Truncate if source is smaller (mark length explicitly)
    target.length = source.length;
  }

  /**
   * Remove snapshots older than the cutoff timestamp
   * O(k) where k is number of elements to remove, not O(n) like shift()
   */
  trimBefore(cutoff: number): void {
    while (this._size > 0) {
      const snapshot = this.buffer[this.head];
      if (this.valid[this.head] && snapshot && snapshot.timestamp < cutoff) {
        this.valid[this.head] = false;
        this.head = (this.head + 1) % this.capacity;
        this._size--;
      } else {
        break;
      }
    }
  }

  /**
   * Get snapshot at logical index (0 = oldest)
   */
  get(index: number): InterpolationSnapshot | null {
    if (index < 0 || index >= this._size) return null;
    const actualIndex = (this.head + index) % this.capacity;
    return this.valid[actualIndex] ? this.buffer[actualIndex]! : null;
  }

  /**
   * Get the last (newest) snapshot
   */
  getLast(): InterpolationSnapshot | null {
    if (this._size === 0) return null;
    const lastIndex = (this.tail - 1 + this.capacity) % this.capacity;
    return this.valid[lastIndex] ? this.buffer[lastIndex]! : null;
  }

  /**
   * Clear the buffer
   */
  clear(): void {
    this.valid.fill(false);
    this.head = 0;
    this.tail = 0;
    this._size = 0;
  }

  /**
   * Find two snapshots for interpolation at the given render time
   * Returns null if not enough data
   */
  findInterpolationPair(renderTime: number): { older: InterpolationSnapshot; newer: InterpolationSnapshot } | null {
    if (this._size < 2) return null;

    for (let i = 0; i < this._size - 1; i++) {
      const older = this.get(i);
      const newer = this.get(i + 1);
      if (older && newer && older.timestamp <= renderTime && newer.timestamp >= renderTime) {
        return { older, newer };
      }
    }
    return null;
  }
}

/**
 * Tracked worm data for rendering
 */
interface TrackedWorm {
  id: string;
  renderer: WormRenderer;
  name: string;
  color: number;
  score: number;
  alive: boolean;
  /** Interpolation buffer for smooth movement (circular buffer) */
  snapshots: SnapshotBuffer;
  /** Is this the local player's worm */
  isLocal: boolean;
  /** Current boost state */
  boosting: boolean;
  /** Current angle in radians */
  angle: number;
  /** Fuel level for boosting (0-100) */
  fuel: number;
  /** Whether this is an AI worm */
  isAI: boolean;
  /** Reference to server worm schema for accessing activePowerups */
  serverWorm: WormSchema;
  /** Server timestamp when spawn protection ends (0 = no protection) */
  spawnProtectionEndTime: number;
}

/**
 * State change event types
 */
/** Kill type for tracking kills by victim type */
export type KillType = 'player' | 'leviathan' | 'apex' | 'hunter' | 'collector';

export interface StateSyncEvents {
  wormAdded: (wormId: string, isLocal: boolean) => void;
  wormRemoved: (wormId: string) => void;
  wormDied: (wormId: string, x: number, y: number, color: number) => void;
  gotKill: (victimId: string, victimName: string, x: number, y: number, victimColor: number, victimType: KillType) => void;
  localPlayerDied: (killedBy: string | null, finalScore: number) => void;
  leaderboardUpdate: (entries: LeaderboardEntry[]) => void;
  foodCountChange: (count: number) => void;
}

// Type definitions for Colyseus schema objects
// These match the server-side schema definitions with Colyseus collection methods

interface SegmentSchema {
  x: number;
  y: number;
  radius: number;
}

interface ActivePowerupSchema {
  powerupType: string;
  remainingTime: number;
  totalDuration: number;
  stacks: number;
}

// Colyseus ArraySchema - callbacks are registered via getStateCallbacks() $ pattern
type ColyseusArraySchema<T> = T[];

interface WormSchema {
  id: string;
  name: string;
  color: number;
  score: number;
  angle: number;
  speed: number;
  boosting: boolean;
  alive: boolean;
  isAI: boolean;
  fuel: number;
  segments: ColyseusArraySchema<SegmentSchema>;
  activePowerups: ColyseusArraySchema<ActivePowerupSchema>;
  // Skin data fields
  skinType: string;
  skinId: string;
  skinPrimaryColor: string;
  skinSecondaryColor: string;
  skinPattern: string;
  skinColors: string; // JSON array of colors
  // Kill streak fields
  killStreak: number;
  killStreakTier: string;
  // Spawn protection
  spawnProtectionEndTime: number;
}

interface FoodSchema {
  id: string;
  x: number;
  y: number;
  radius: number;
  color: number;
  value: number;
  foodType: string;
}

interface LeaderboardSchema {
  id: string;
  name: string;
  score: number;
  rank: number;
}

interface PowerupSchema {
  id: string;
  powerupType: string;
  x: number;
  y: number;
  radius: number;
  color: number;
}

// Colyseus MapSchema - callbacks are registered via getStateCallbacks() $ pattern
interface ColyseusMapSchema<T> {
  forEach: (callback: (item: T, key: string) => void) => void;
  get: (key: string) => T | undefined;
  size: number;
}

interface GameState {
  worms: ColyseusMapSchema<WormSchema>;
  foods: ColyseusMapSchema<FoodSchema>;
  powerups: ColyseusMapSchema<PowerupSchema>;
  leaderboard: ColyseusArraySchema<LeaderboardSchema>;
  serverTime: number;
}

/**
 * StateSync - Synchronizes server state with client renderers
 *
 * Handles:
 * - Worm creation/destruction when players join/leave
 * - Interpolation buffer for smooth remote entity movement
 * - Food state synchronization
 * - Leaderboard updates
 */
export class StateSync {
  /** Reference to the current room */
  private room: Room | null = null;

  /** Local player's session ID */
  private localSessionId: string | null = null;

  /** Container for worm renderers */
  private wormsContainer: Container;

  /** Food renderer instance */
  private foodRenderer: FoodRenderer;

  /** Powerup renderer instance */
  private powerupRenderer: PowerupRenderer | null = null;

  /** Tracked worms by session ID */
  private worms: Map<string, TrackedWorm> = new Map();

  /** Event listeners */
  private listeners: Map<string, Set<Function>> = new Map();

  /** Current leaderboard */
  private leaderboard: LeaderboardEntry[] = [];

  /** Interpolation buffer time in ms */
  private interpolationBufferMs: number;

  /** Worms currently in death animation (not yet destroyed) */
  private dyingWorms: Map<string, WormRenderer> = new Map();

  /** Whether syncing is active */
  private active: boolean = false;

  /** Particle system for effects */
  private particleSystem: ParticleSystem | null = null;

  /** Local player's accessory to apply when worm is created */
  private localPlayerAccessory: AccessoryType = AccessoryType.NONE;

  /** Colyseus state callback getter (0.16+ API) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private $: ReturnType<typeof getStateCallbacks> | null = null;

  constructor(
    wormsContainer: Container,
    foodRenderer: FoodRenderer,
    powerupRenderer: PowerupRenderer,
    interpolationBufferMs: number = INTERPOLATION_BUFFER_MS,
    particleSystem?: ParticleSystem
  ) {
    this.wormsContainer = wormsContainer;
    this.foodRenderer = foodRenderer;
    this.powerupRenderer = powerupRenderer;
    this.interpolationBufferMs = interpolationBufferMs;
    this.particleSystem = particleSystem || null;
  }

  /**
   * Set particle system for death effects
   */
  setParticleSystem(particleSystem: ParticleSystem): void {
    this.particleSystem = particleSystem;
  }

  /**
   * Set the local player's accessory
   * This will be applied when the local worm is created, or immediately if it already exists
   */
  setLocalPlayerAccessory(accessory: AccessoryType): void {
    this.localPlayerAccessory = accessory;

    // If local worm already exists, apply immediately
    if (this.localSessionId) {
      const localWorm = this.worms.get(this.localSessionId);
      if (localWorm && accessory !== AccessoryType.NONE) {
        localWorm.renderer.setAccessory(accessory);
      }
    }
  }

  /**
   * Start syncing with a room
   */
  startSync(room: Room, localSessionId: string): void {
    if (this.active) {
      this.stopSync();
    }

    this.room = room;
    this.localSessionId = localSessionId;
    this.active = true;

    console.log('[StateSync] Starting sync', {
      localSessionId,
      stateWorms: room.state?.worms ? 'available' : 'not available',
    });

    this.setupStateListeners();
    this.setupMessageListeners();
  }

  /**
   * Stop syncing
   */
  stopSync(): void {
    this.active = false;

    // Clean up all worm renderers
    this.worms.forEach((trackedWorm) => {
      this.wormsContainer.removeChild(trackedWorm.renderer.container);
      trackedWorm.renderer.destroy();
    });
    this.worms.clear();

    // Clean up dying worms
    this.dyingWorms.forEach((renderer) => {
      this.wormsContainer.removeChild(renderer.container);
      renderer.destroy();
    });
    this.dyingWorms.clear();

    // Clear food
    this.foodRenderer.clear();

    // Clear powerups
    if (this.powerupRenderer) {
      this.powerupRenderer.clear();
    }

    this.room = null;
    this.localSessionId = null;
    this.$ = null;
  }

  /**
   * Setup listeners for Colyseus schema state changes
   * Uses getStateCallbacks() API from colyseus.js 0.16+
   */
  private setupStateListeners(): void {
    if (!this.room || !this.room.state) return;

    const state = this.room.state as GameState;

    // Validate state has required collections
    if (!state.worms) {
      console.error('[StateSync] State worms collection not available.');
      throw new Error('Failed to initialize state sync: worms collection not found');
    }

    // Use getStateCallbacks to register collection callbacks (Colyseus 0.16+ API)
    this.$ = getStateCallbacks(this.room);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const $ = this.$ as any;

    // Listen to worms collection changes
    $(state).worms.onAdd((worm: WormSchema, sessionId: string) => {
      this.onWormAdded(worm, sessionId);
    });

    $(state).worms.onRemove((_worm: WormSchema, sessionId: string) => {
      this.onWormRemoved(sessionId);
    });

    // Listen to foods collection changes
    $(state).foods.onAdd((food: FoodSchema, foodId: string) => {
      this.onFoodAdded(food, foodId);
    });

    $(state).foods.onRemove((_food: FoodSchema, foodId: string) => {
      this.onFoodRemoved(foodId);
    });

    // Listen to powerups collection changes
    $(state).powerups.onAdd((powerup: PowerupSchema, powerupId: string) => {
      this.onPowerupAdded(powerup, powerupId);
    });

    $(state).powerups.onRemove((_powerup: PowerupSchema, powerupId: string) => {
      this.onPowerupRemoved(powerupId);
    });

    // Listen to leaderboard changes
    $(state).leaderboard.onAdd(() => {
      this.updateLeaderboard();
    });

    $(state).leaderboard.onRemove(() => {
      this.updateLeaderboard();
    });

    // Initial leaderboard
    this.updateLeaderboard();
  }

  /**
   * Setup message listeners for server events
   */
  private setupMessageListeners(): void {
    if (!this.room) return;

    // Listen for player death messages
    this.room.onMessage(MessageType.PLAYER_DIED, (data: {
      playerId: string;
      killedBy: string | null;
      finalScore: number;
    }) => {
      if (data.playerId === this.localSessionId) {
        this.emit('localPlayerDied', data.killedBy, data.finalScore);
      }

      // Check if local player got a kill
      if (data.killedBy === this.localSessionId && data.playerId !== this.localSessionId) {
        const victim = this.worms.get(data.playerId);
        const victimName = victim?.name ?? 'Unknown';
        const victimPos = victim?.renderer.getHeadPosition();
        const victimColor = victim?.color ?? 0xff6666;

        // Determine victim type for kill tracking
        let victimType: KillType = 'player';
        if (victim?.isAI) {
          // Look up AI character to get personality type
          const aiCharacter = getAICharacterByName(victimName);
          if (aiCharacter) {
            const personalityType = aiCharacter.personalityType.toLowerCase();
            if (personalityType === 'legendary') {
              victimType = 'leviathan';
            } else if (personalityType === 'apex' || personalityType === 'hunter' || personalityType === 'collector') {
              victimType = personalityType as KillType;
            }
          }
        }

        this.emit('gotKill', data.playerId, victimName, victimPos?.x ?? 0, victimPos?.y ?? 0, victimColor, victimType);
      }

      // Update worm alive state
      const trackedWorm = this.worms.get(data.playerId);
      if (trackedWorm) {
        trackedWorm.alive = false;
        trackedWorm.renderer.setVisible(false);
      }
    });
  }

  /**
   * Handle worm added to state
   */
  private onWormAdded(worm: WormSchema, sessionId: string): void {
    // Check if we already have this worm
    if (this.worms.has(sessionId)) {
      return;
    }

    const isLocal = sessionId === this.localSessionId;

    console.log('[StateSync] Worm added', {
      sessionId,
      isLocal,
      localSessionId: this.localSessionId,
      wormName: worm.name,
      wormAlive: worm.alive,
    });

    // Extract skin data from worm schema
    const skinData = this.extractSkinData(worm);

    // Create renderer for the worm with skin data
    const renderer = new WormRenderer({
      color: worm.color,
      segmentCount: worm.segments.length || 10,
      radius: 15,
      segmentSpacing: 20,
      ...(skinData && { skin: skinData }),
    });

    // Mark as local player if this is our worm
    if (isLocal) {
      renderer.setIsLocalPlayer(true);
      // Apply local player's accessory if set
      if (this.localPlayerAccessory !== AccessoryType.NONE) {
        renderer.setAccessory(this.localPlayerAccessory);
      }
    }

    // Apply initial kill streak tier if any
    if (worm.killStreakTier) {
      renderer.setKillStreakTier(worm.killStreakTier);
    }

    // Add to container
    this.wormsContainer.addChild(renderer.container);

    // Initialize position from state
    console.log('[StateSync] Initial segments for', sessionId, ':', worm.segments.length);
    if (worm.segments.length > 0) {
      const segments = this.extractSegments(worm.segments);
      renderer.updateFromSegments(segments);
      console.log('[StateSync] Applied initial segments', { head: segments[0] });
    }

    // Track the worm
    const trackedWorm: TrackedWorm = {
      id: sessionId,
      renderer,
      name: worm.name,
      color: worm.color,
      score: worm.score,
      alive: worm.alive,
      snapshots: new SnapshotBuffer(20), // ~600ms at 30Hz server tick
      isLocal,
      boosting: worm.boosting,
      angle: worm.angle,
      fuel: worm.fuel ?? 100,
      isAI: worm.isAI ?? false,
      serverWorm: worm,
      spawnProtectionEndTime: worm.spawnProtectionEndTime ?? 0,
    };

    // Apply initial spawn protection visual if active
    if (worm.spawnProtectionEndTime > Date.now()) {
      renderer.setSpawnProtection(true);
    }

    this.worms.set(sessionId, trackedWorm);

    // Listen to changes on this worm
    this.setupWormListeners(worm, trackedWorm);

    // Emit event
    this.emit('wormAdded', sessionId, isLocal);

    // Update same-skin indicators for all worms
    // This handles both when local player joins and when enemies with same skin join
    this.updateSameSkinIndicators();
  }

  /**
   * Extract skin data from worm schema
   */
  private extractSkinData(worm: WormSchema): WormSkinData | undefined {
    // Check if worm has valid skin data
    if (!worm.skinType || worm.skinType === 'color') {
      // Default color skin - no special skin data needed
      return undefined;
    }

    const skinData: WormSkinData = {
      type: worm.skinType,
      id: worm.skinId,
      primaryColor: worm.skinPrimaryColor,
    };

    // Add optional fields if present
    if (worm.skinSecondaryColor) {
      skinData.secondaryColor = worm.skinSecondaryColor;
    }

    if (worm.skinPattern) {
      skinData.pattern = worm.skinPattern;
    }

    if (worm.skinColors) {
      try {
        skinData.colors = JSON.parse(worm.skinColors);
      } catch {
        // Invalid JSON, ignore
      }
    }

    return skinData;
  }

  /**
   * Setup listeners for changes on a specific worm
   * Uses getStateCallbacks() $ pattern for Colyseus 0.16+ API
   */
  private setupWormListeners(worm: WormSchema, trackedWorm: TrackedWorm): void {
    if (!this.$) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const $ = this.$ as any;

    // Listen to property changes using $ callback pattern
    $(worm).listen('score', (value: unknown) => {
      trackedWorm.score = value as number;
    });

    $(worm).listen('alive', (value: unknown) => {
      const alive = value as boolean;
      trackedWorm.alive = alive;
      trackedWorm.renderer.setVisible(alive);
    });

    $(worm).listen('boosting', (value: unknown) => {
      trackedWorm.boosting = value as boolean;
      trackedWorm.renderer.setBoostEffect(value as boolean);
    });

    $(worm).listen('angle', (value: unknown) => {
      trackedWorm.angle = value as number;
    });

    $(worm).listen('fuel', (value: unknown) => {
      trackedWorm.fuel = value as number;
    });

    // Listen to kill streak tier changes for visual effects
    $(worm).listen('killStreakTier', (value: unknown) => {
      trackedWorm.renderer.setKillStreakTier(value as string || '');
    });

    // Listen to spawn protection changes for ghost visual effect
    $(worm).listen('spawnProtectionEndTime', (value: unknown) => {
      const endTime = value as number;
      trackedWorm.spawnProtectionEndTime = endTime;
      const isProtected = endTime > Date.now();
      trackedWorm.renderer.setSpawnProtection(isProtected);
    });

    // Listen to segment changes for remote players
    // For the local player, we don't interpolate - we use client-side prediction
    if (!trackedWorm.isLocal) {
      $(worm).segments.onAdd(() => {
        this.captureSnapshot(worm, trackedWorm);
      });

      $(worm).segments.onChange(() => {
        this.captureSnapshot(worm, trackedWorm);
      });

      // Also listen to angle for direction
      $(worm).listen('angle', () => {
        this.captureSnapshot(worm, trackedWorm);
      });
    } else {
      // For local player, still update segments but directly (no interpolation)
      // Must listen to both onAdd (for initial segments) and onChange (for updates)
      let segmentUpdateCount = 0;
      const updateLocalSegments = (source: string) => {
        const segments = this.extractSegments(worm.segments);
        if (segments.length > 0) {
          trackedWorm.renderer.updateFromSegments(segments);
          segmentUpdateCount++;
          if (segmentUpdateCount <= 3) {
            console.log('[StateSync] Local player segments updated via', source, { count: segments.length, head: segments[0] });
          }
        }
      };

      $(worm).segments.onAdd(() => {
        updateLocalSegments('onAdd');
      });

      $(worm).segments.onChange(() => {
        updateLocalSegments('onChange');
      });
    }
  }

  /**
   * Capture a snapshot for interpolation with velocity calculation
   * Uses pre-allocated snapshot slots to avoid GC pressure
   */
  private captureSnapshot(worm: WormSchema, trackedWorm: TrackedWorm): void {
    const now = Date.now();
    const prevSnapshot = trackedWorm.snapshots.getLast();
    const timeDelta = prevSnapshot ? (now - prevSnapshot.timestamp) / 1000 : 0;

    // Get the pre-allocated slot from the buffer
    const snapshot = trackedWorm.snapshots.getNextSlot();
    snapshot.timestamp = now;
    snapshot.angle = worm.angle;
    snapshot.boosting = worm.boosting;

    // Ensure segments array is large enough (grow as needed, reuse existing objects)
    const segmentCount = worm.segments.length;
    while (snapshot.segments.length < segmentCount) {
      snapshot.segments.push({ x: 0, y: 0, radius: 0, vx: 0, vy: 0 });
    }
    // Truncate if worm shrunk
    snapshot.segments.length = segmentCount;

    // Fill segments in-place with velocity calculation
    for (let i = 0; i < segmentCount; i++) {
      const seg = worm.segments[i];
      if (!seg) continue;

      const target = snapshot.segments[i]!;
      target.x = seg.x;
      target.y = seg.y;
      target.radius = seg.radius;

      // Calculate velocity from previous snapshot
      if (prevSnapshot && prevSnapshot.segments[i] && timeDelta > 0.001) {
        const prevSeg = prevSnapshot.segments[i]!;
        target.vx = (seg.x - prevSeg.x) / timeDelta;
        target.vy = (seg.y - prevSeg.y) / timeDelta;
      } else {
        target.vx = 0;
        target.vy = 0;
      }
    }

    // Commit the snapshot to the buffer
    trackedWorm.snapshots.commitPush();

    // Trim old snapshots (O(k) instead of O(n) shift operations)
    const cutoff = now - 500;
    trackedWorm.snapshots.trimBefore(cutoff);
  }

  /**
   * Extract segments from schema array
   */
  private extractSegments(segments: ColyseusArraySchema<SegmentSchema>): SegmentData[] {
    const result: SegmentData[] = [];
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (seg) {
        result.push({
          x: seg.x,
          y: seg.y,
          radius: seg.radius,
        });
      }
    }
    return result;
  }

  /**
   * Handle worm removed from state
   */
  private onWormRemoved(sessionId: string): void {
    const trackedWorm = this.worms.get(sessionId);
    if (!trackedWorm) return;

    // Get worm position and color before removing
    const renderer = trackedWorm.renderer;
    const headPos = renderer.getHeadPosition();
    const bodyPoints = renderer.getBodyPoints();
    const wormColor = trackedWorm.color;

    // Remove from tracking immediately (no longer participates in game logic)
    this.worms.delete(sessionId);

    // Spawn death explosion effect at worm position
    if (this.particleSystem && headPos) {
      // Get segment positions for chain explosion effect
      const segmentPositions = bodyPoints.map((p) => ({ x: p.x, y: p.y }));
      this.particleSystem.spawnDeathExplosion(
        headPos.x,
        headPos.y,
        wormColor,
        segmentPositions
      );
    }

    // Don't animate local player death (handled by death screen)
    if (trackedWorm.isLocal) {
      this.wormsContainer.removeChild(renderer.container);
      renderer.destroy();
      this.emit('wormRemoved', sessionId);
      return;
    }

    // Start death animation for remote players (fade-out over 1 second)
    this.dyingWorms.set(sessionId, renderer);

    renderer.startDeathAnimation(() => {
      // After animation completes, clean up
      this.wormsContainer.removeChild(renderer.container);
      renderer.destroy();
      this.dyingWorms.delete(sessionId);
    }, 1000);

    // Emit event with position info for additional effects
    this.emit('wormRemoved', sessionId);
    this.emit('wormDied', sessionId, headPos?.x ?? 0, headPos?.y ?? 0, wormColor);
  }

  /**
   * Handle food added to state
   */
  private onFoodAdded(food: FoodSchema, foodId: string): void {
    this.foodRenderer.addFood({
      id: foodId,
      x: food.x,
      y: food.y,
      radius: food.radius,
      color: food.color,
      foodType: food.foodType as import('@wooorms/shared').FoodType,
    });

    this.emit('foodCountChange', this.foodRenderer.getFoodCount());
  }

  /**
   * Handle food removed from state
   */
  private onFoodRemoved(foodId: string): void {
    this.foodRenderer.removeFood(foodId);
    this.emit('foodCountChange', this.foodRenderer.getFoodCount());
  }

  /**
   * Handle powerup added to state
   */
  private onPowerupAdded(powerup: PowerupSchema, powerupId: string): void {
    if (!this.powerupRenderer) return;

    this.powerupRenderer.addPowerup({
      id: powerupId,
      x: powerup.x,
      y: powerup.y,
      radius: powerup.radius,
      color: powerup.color,
      powerupType: powerup.powerupType as import('@wooorms/shared').PowerupType,
    });
  }

  /**
   * Handle powerup removed from state
   */
  private onPowerupRemoved(powerupId: string): void {
    if (!this.powerupRenderer) return;

    this.powerupRenderer.removePowerup(powerupId);
  }

  /**
   * Update leaderboard from state
   */
  private updateLeaderboard(): void {
    if (!this.room || !this.room.state) return;

    const state = this.room.state as GameState;
    this.leaderboard = [];

    for (let i = 0; i < state.leaderboard.length; i++) {
      const entry = state.leaderboard[i];
      if (entry) {
        this.leaderboard.push({
          id: entry.id,
          name: entry.name,
          score: entry.score,
          rank: entry.rank,
        });
      }
    }

    this.emit('leaderboardUpdate', this.leaderboard);
  }

  /**
   * Update interpolation for all remote entities
   * Should be called each frame with delta time
   * @param dt Delta time in seconds
   * @param cullingBounds Optional culling bounds for per-segment visibility optimization
   */
  update(dt: number, cullingBounds?: { left: number; right: number; top: number; bottom: number }): void {
    if (!this.active) return;

    const renderTime = Date.now() - this.interpolationBufferMs;

    // Interpolate each remote worm
    this.worms.forEach((trackedWorm) => {
      if (trackedWorm.isLocal || !trackedWorm.alive) return;

      this.interpolateWorm(trackedWorm, renderTime);
    });

    // Update dying worm animations (fade-out)
    this.dyingWorms.forEach((renderer) => {
      renderer.updateDeathAnimation(dt);
    });

    // Update skin animations for all worms (animated patterns)
    // Pass culling bounds for per-segment visibility optimization
    const currentTime = Date.now();
    this.worms.forEach((trackedWorm) => {
      if (cullingBounds) {
        trackedWorm.renderer.setCullingBounds(cullingBounds);
      }
      trackedWorm.renderer.updateSkinAnimation(dt);

      // Check if spawn protection has expired (client-side check for smooth visual transition)
      if (trackedWorm.spawnProtectionEndTime > 0 && trackedWorm.spawnProtectionEndTime <= currentTime) {
        trackedWorm.spawnProtectionEndTime = 0;
        trackedWorm.renderer.setSpawnProtection(false);
      }
    });
  }

  /**
   * Hermite interpolation for smooth curves
   * Produces C1 continuous curves (smooth tangents at control points)
   */
  private hermite(p0: number, v0: number, p1: number, v1: number, t: number, dt: number): number {
    // Scale velocities by time interval for proper tangent magnitude
    const m0 = v0 * dt;
    const m1 = v1 * dt;

    const t2 = t * t;
    const t3 = t2 * t;

    // Hermite basis functions
    const h00 = 2 * t3 - 3 * t2 + 1;  // Position at p0
    const h10 = t3 - 2 * t2 + t;       // Tangent at p0
    const h01 = -2 * t3 + 3 * t2;      // Position at p1
    const h11 = t3 - t2;               // Tangent at p1

    return h00 * p0 + h10 * m0 + h01 * p1 + h11 * m1;
  }

  /**
   * Interpolate a worm's position between snapshots using Hermite interpolation
   * Falls back to velocity extrapolation when behind latest snapshot
   */
  private interpolateWorm(trackedWorm: TrackedWorm, renderTime: number): void {
    const snapshots = trackedWorm.snapshots;

    if (snapshots.size < 2) {
      // Not enough data to interpolate, use latest snapshot
      if (snapshots.size === 1) {
        const single = snapshots.get(0);
        if (single) trackedWorm.renderer.updateFromSegments(single.segments);
      }
      return;
    }

    // Find the two snapshots to interpolate between using the circular buffer
    const interpolationPair = snapshots.findInterpolationPair(renderTime);
    let older: InterpolationSnapshot | null = interpolationPair?.older ?? null;
    let newer: InterpolationSnapshot | null = interpolationPair?.newer ?? null;

    // If renderTime is ahead of all snapshots, extrapolate from latest
    const latest = snapshots.getLast();
    if (!older || !newer) {
      if (!latest) return;

      if (renderTime > latest.timestamp) {
        // Extrapolate using velocity from latest snapshot
        const extrapolateTime = (renderTime - latest.timestamp) / 1000;
        const maxExtrapolate = 0.1; // Max 100ms extrapolation
        const clampedTime = Math.min(extrapolateTime, maxExtrapolate);

        const extrapolatedSegments: SegmentData[] = latest.segments.map(seg => ({
          x: seg.x + seg.vx * clampedTime,
          y: seg.y + seg.vy * clampedTime,
          radius: seg.radius,
        }));

        trackedWorm.renderer.updateFromSegments(extrapolatedSegments);
        trackedWorm.renderer.setBoostEffect(latest.boosting);
        return;
      }

      // Use latest if can't find pair (shouldn't happen often)
      trackedWorm.renderer.updateFromSegments(latest.segments);
      return;
    }

    // Calculate interpolation factor
    const totalTime = newer.timestamp - older.timestamp;
    const elapsedTime = renderTime - older.timestamp;
    const t = totalTime > 0 ? Math.min(1, Math.max(0, elapsedTime / totalTime)) : 0;
    const dtSeconds = totalTime / 1000;

    // Hermite interpolate segments for smooth curves
    const interpolatedSegments: SegmentData[] = [];
    const segmentCount = Math.min(older.segments.length, newer.segments.length);

    for (let i = 0; i < segmentCount; i++) {
      const oldSeg = older.segments[i]!;
      const newSeg = newer.segments[i]!;

      // Use Hermite interpolation for position (smoother than linear)
      const x = this.hermite(oldSeg.x, oldSeg.vx, newSeg.x, newSeg.vx, t, dtSeconds);
      const y = this.hermite(oldSeg.y, oldSeg.vy, newSeg.y, newSeg.vy, t, dtSeconds);

      // Linear interpolation for radius (doesn't need smooth curves)
      const radius = oldSeg.radius + (newSeg.radius - oldSeg.radius) * t;

      interpolatedSegments.push({ x, y, radius });
    }

    // Update renderer with interpolated segments
    trackedWorm.renderer.updateFromSegments(interpolatedSegments);

    // Apply boost effect
    trackedWorm.renderer.setBoostEffect(newer.boosting);
  }

  /**
   * Get the local player's worm renderer
   */
  getLocalWormRenderer(): WormRenderer | null {
    if (!this.localSessionId) return null;
    return this.worms.get(this.localSessionId)?.renderer ?? null;
  }

  /**
   * Get the local player's worm data
   */
  getLocalWorm(): TrackedWorm | null {
    if (!this.localSessionId) return null;
    return this.worms.get(this.localSessionId) ?? null;
  }

  /**
   * Check if the local player has a specific powerup active
   * @param powerupType The powerup type to check for (e.g., 'VISION')
   * @returns True if the powerup is active with remaining time
   */
  hasLocalPlayerPowerup(powerupType: string): boolean {
    const localWorm = this.getLocalWorm();
    if (!localWorm) return false;

    const activePowerups = localWorm.serverWorm.activePowerups;
    if (!activePowerups) return false;

    for (let i = 0; i < activePowerups.length; i++) {
      const ap = activePowerups[i];
      if (ap && ap.powerupType === powerupType && ap.remainingTime > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get worm renderer by session ID
   */
  getWormRenderer(sessionId: string): WormRenderer | null {
    return this.worms.get(sessionId)?.renderer ?? null;
  }

  /**
   * Get all tracked worms
   */
  getAllWorms(): TrackedWorm[] {
    return Array.from(this.worms.values());
  }

  /**
   * Iterate over all tracked worms
   */
  forEachWorm(callback: (worm: TrackedWorm, wormId: string) => void): void {
    this.worms.forEach((worm, wormId) => callback(worm, wormId));
  }

  /**
   * Get current leaderboard
   */
  getLeaderboard(): LeaderboardEntry[] {
    return this.leaderboard;
  }

  /**
   * Update same-skin indicators for all enemy worms
   * Call this when a new worm is added or skins change
   */
  updateSameSkinIndicators(): void {
    const localWorm = this.getLocalWorm();
    if (!localWorm) return;

    const localSkin = localWorm.renderer.getSkin();

    this.worms.forEach((trackedWorm) => {
      if (trackedWorm.isLocal) return;

      const enemySkin = trackedWorm.renderer.getSkin();
      const isSameSkin = this.areSkinsSame(localSkin, enemySkin);
      trackedWorm.renderer.setSameSkinEnemy(isSameSkin);
    });
  }

  /**
   * Compare two skins to determine if they are the same
   */
  private areSkinsSame(skin1: WormSkinData | null, skin2: WormSkinData | null): boolean {
    // Both null or undefined - default skins
    if (!skin1 && !skin2) return true;

    // One is null, other isn't
    if (!skin1 || !skin2) return false;

    // Must have same type
    if (skin1.type !== skin2.type) return false;

    // Must have same id
    if (skin1.id !== skin2.id) return false;

    // Must have same primary color
    if (skin1.primaryColor !== skin2.primaryColor) return false;

    // Must have same pattern (if any)
    if (skin1.pattern !== skin2.pattern) return false;

    // Must have same secondary color (if any)
    if (skin1.secondaryColor !== skin2.secondaryColor) return false;

    // Must have same colors array (for flags)
    const colors1 = skin1.colors || [];
    const colors2 = skin2.colors || [];
    if (colors1.length !== colors2.length) return false;
    for (let i = 0; i < colors1.length; i++) {
      if (colors1[i] !== colors2[i]) return false;
    }

    return true;
  }

  /**
   * Check if the local player is alive
   */
  isLocalPlayerAlive(): boolean {
    const localWorm = this.getLocalWorm();
    return localWorm?.alive ?? false;
  }

  /**
   * Add an event listener
   */
  on<K extends keyof StateSyncEvents>(event: K, callback: StateSyncEvents[K]): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Remove an event listener
   */
  off<K extends keyof StateSyncEvents>(event: K, callback: StateSyncEvents[K]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Emit an event
   */
  private emit(event: string, ...args: unknown[]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopSync();
    this.listeners.clear();
  }
}
